import React from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { X, Settings } from 'lucide-react';

// Default param values
const PARAM_DEFAULTS = {
  chordsColumn: 'true', section: 'true', part: 'true', bars: 'true',
  notes: 'true', chords: 'true', lineup: 'true', notation: 'true',
  lyrics: 'true', lyricsColumn: 'true', pagebreak: 'true', energy: 'true',
  minify: 'false', header: 'false', rotateSection: 'false'
};

// Mode presets - overrides on top of defaults
const MODE_PRESETS = {
  singer: {
    label: 'Singer',
    overrides: { chordsColumn: 'false', part: 'false', bars: 'false', chords: 'false', lineup: 'false', notation: 'false' }
  },
  instrument: {
    label: 'Instrument',
    overrides: { part: 'false', bars: 'false' }
  },
  mini: {
    label: 'Mini',
    overrides: { minify: 'true', chordsColumn: 'false', bars: 'false', part: 'false', notes: 'false', chords: 'false', energy: 'false' }
  },
  maxi: {
    label: 'Maxi',
    overrides: { header: 'true' }
  }
};

// Compute full params for each mode
Object.values(MODE_PRESETS).forEach(mode => {
  mode.params = { ...PARAM_DEFAULTS, ...mode.overrides };
});

/**
 * PrintViewControls component for the perform view
 * Provides buttons to control performance rendering options directly in the perform view
 */
const PrintViewControls = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { sheetId } = useParams();
  const navigate = useNavigate();

  // Track modal state via URL parameter to persist across re-renders
  const isOpen = searchParams.get('menu') === 'open';

  // Get current values from URL parameters
  const showChords = searchParams.get('chords') !== 'false';  // Default to true
  const showLyrics = searchParams.get('lyrics') !== 'false';  // Default to true
  const showLineup = searchParams.get('lineup') !== 'false';  // Default to true
  const showNotation = searchParams.get('notation') !== 'false';  // Default to true
  const minifyMode = searchParams.get('minify') === 'true';  // Default to false
  const pageBreakMode = searchParams.get('pagebreak') !== 'false';  // Default to true

  // Column visibility controls with custom defaults
  const showSection = searchParams.get('section') !== 'false';  // Default to true
  const showPart = searchParams.get('part') !== 'false';  // Default to true
  const showBars = searchParams.get('bars') !== 'false';  // Default to true
  const showNotes = searchParams.get('notes') !== 'false';  // Default to true
  const showLyricsColumn = searchParams.get('lyricsColumn') !== 'false';  // Default to true
  const showHeaders = searchParams.get('header') === 'true';  // Default to false
  const rotateSection = searchParams.get('rotateSection') === 'true';  // Default to false
  const showEnergy = searchParams.get('energy') !== 'false';  // Default to true
  const showChordsColumn = searchParams.get('chordsColumn') !== 'false';  // Default to true

  // Handle chords toggle
  const handleChordsToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('chords', showChords ? 'false' : 'true');
    setSearchParams(newParams);
  };

  // Handle lyrics toggle
  const handleLyricsToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('lyrics', showLyrics ? 'false' : 'true');
    setSearchParams(newParams);
  };

  // Handle lineup toggle
  const handleLineupToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('lineup', showLineup ? 'false' : 'true');
    setSearchParams(newParams);
  };

  // Handle notation toggle
  const handleNotationToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('notation', showNotation ? 'false' : 'true');
    setSearchParams(newParams);
  };

  // Handle minify toggle
  const handleMinifyToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('minify', minifyMode ? 'false' : 'true');
    setSearchParams(newParams);
  };

  // Handle page break toggle
  const handlePageBreakToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pagebreak', pageBreakMode ? 'false' : 'true');
    setSearchParams(newParams);
  };

  // Handle column toggles
  const handleSectionToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('section', showSection ? 'false' : 'true');
    setSearchParams(newParams);
  };

  const handlePartToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('part', showPart ? 'false' : 'true');
    setSearchParams(newParams);
  };

  const handleBarsToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('bars', showBars ? 'false' : 'true');
    setSearchParams(newParams);
  };

  const handleNotesToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('notes', showNotes ? 'false' : 'true');
    setSearchParams(newParams);
  };

  const handleLyricsColumnToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('lyricsColumn', showLyricsColumn ? 'false' : 'true');
    setSearchParams(newParams);
  };

  const handleHeadersToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('header', showHeaders ? 'false' : 'true');
    setSearchParams(newParams);
  };

  const handleRotateSectionToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('rotateSection', rotateSection ? 'false' : 'true');
    setSearchParams(newParams);
  };

  const handleEnergyToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('energy', showEnergy ? 'false' : 'true');
    setSearchParams(newParams);
  };

  const handleChordsColumnToggle = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('chordsColumn', showChordsColumn ? 'false' : 'true');
    setSearchParams(newParams);
  };

  // Apply a mode preset
  const applyMode = (modeKey) => {
    const newParams = new URLSearchParams(searchParams);
    const preset = MODE_PRESETS[modeKey];
    Object.entries(preset.params).forEach(([key, value]) => {
      newParams.set(key, value);
    });
    newParams.delete('menu'); // Close modal when applying mode
    setSearchParams(newParams);
  };

  // Detect which mode is currently active (if any)
  const getActiveMode = () => {
    for (const [key, preset] of Object.entries(MODE_PRESETS)) {
      const matches = Object.entries(preset.params).every(([param, value]) => {
        const current = searchParams.get(param) ?? PARAM_DEFAULTS[param];
        return current === value;
      });
      if (matches) return key;
    }
    return null;
  };

  const activeMode = getActiveMode();

  // Handle opening the modal
  const handleOpenModal = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('menu', 'open');
    setSearchParams(newParams);
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('menu');
    setSearchParams(newParams);
  };

  return (
    <>
      {/* Menu Button - Fixed at top right */}
      <button
        onClick={handleOpenModal}
        className="no-print fixed top-4 right-4 z-[9998] p-2 bg-white text-gray-700 border border-gray-300 rounded-lg shadow-md hover:bg-gray-50 hover:shadow-lg transition-all"
        title="Display Settings"
      >
        <Settings size={18} />
      </button>

      {/* Modal */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="no-print fixed inset-0 bg-black bg-opacity-50 z-[9999] transition-opacity duration-200"
          />

          {/* Modal Content */}
          <div className="no-print fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[10000] w-[90vw] max-w-[500px] max-h-[85vh] overflow-y-auto bg-white rounded-lg shadow-2xl">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900">Display Settings</h2>
                <button
                  onClick={handleCloseModal}
                  className="p-1 text-gray-500 hover:text-gray-700 transition-colors rounded-md hover:bg-gray-100"
                  title="Close"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-5">
                {/* Mode presets */}
                <div>
                  <div className="text-sm font-bold mb-3 text-gray-700">
                    Mode:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(MODE_PRESETS).map(([key, preset]) => (
                      <button
                        key={key}
                        onClick={() => applyMode(key)}
                        className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                          activeMode === key
                            ? 'bg-gray-800 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Parts section */}
                <div>
                  <div className="text-sm font-bold mb-3 text-gray-700">
                    Parts:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleLyricsToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        showLyrics
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Lyrics
                    </button>

                    <button
                      onClick={handleChordsToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        showChords
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Chords
                    </button>

                    <button
                      onClick={handleLineupToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        showLineup
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Lineup
                    </button>

                    <button
                      onClick={handleNotationToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        showNotation
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Notation
                    </button>
                  </div>
                </div>

                {/* Display settings */}
                <div className="pt-3 border-t border-gray-200">
                  <div className="text-sm font-bold mb-3 text-gray-700">
                    Display:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleMinifyToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        minifyMode
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Minify
                    </button>

                    <button
                      onClick={handlePageBreakToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        pageBreakMode
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Page Break
                    </button>

                    {showSection && (
                      <button
                        onClick={handleRotateSectionToggle}
                        className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                          rotateSection
                            ? 'bg-gray-800 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title="Rotate section names 90 degrees"
                      >
                        Rotate Sections
                      </button>
                    )}

                    <button
                      onClick={handleEnergyToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        showEnergy
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Energy
                    </button>
                  </div>
                </div>

                {/* Column visibility toggles */}
                <div className="pt-3 border-t border-gray-200">
                  <div className="text-sm font-bold mb-3 text-gray-700">
                    Columns:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleSectionToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        showSection
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Section
                    </button>

                    <button
                      onClick={handlePartToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        showPart
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Part
                    </button>

                    <button
                      onClick={handleBarsToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        showBars
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Bars
                    </button>

                    <button
                      onClick={handleChordsColumnToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        showChordsColumn
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Chords
                    </button>

                    <button
                      onClick={handleLyricsColumnToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        showLyricsColumn
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Lyrics
                    </button>

                    <button
                      onClick={handleNotesToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        showNotes
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Notes
                    </button>

                    <button
                      onClick={handleHeadersToggle}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        showHeaders
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Headers
                    </button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="pt-3 border-t border-gray-200">
                  <button
                    onClick={() => window.print()}
                    className="w-full px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    Print PDF
                  </button>

                  <button
                    onClick={() => navigate(`/sheet/${sheetId}`)}
                    className="w-full mt-2 px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    Edit Sheet
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PrintViewControls;
